import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
    Users,
    UserPlus,
    Mail,
    Crown,
    Shield,
    User,
    Eye,
    MoreVertical,
    Trash2,
    ChevronDown,
    Copy,
    Check,
    Loader2,
    AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';

type Role = 'owner' | 'admin' | 'member' | 'viewer';

const roleConfig: Record<Role, { label: string; icon: typeof Crown; color: string }> = {
    owner: { label: 'Chủ sở hữu', icon: Crown, color: 'text-yellow-600 bg-yellow-100' },
    admin: { label: 'Quản trị viên', icon: Shield, color: 'text-purple-600 bg-purple-100' },
    member: { label: 'Thành viên', icon: User, color: 'text-blue-600 bg-blue-100' },
    viewer: { label: 'Người xem', icon: Eye, color: 'text-neutral-600 bg-neutral-100' },
};

export default function TeamSettingsPage() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const workspace = useAuthStore((s) => s.workspace);
    const currentUser = useAuthStore((s) => s.user);

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
    const [copiedLink, setCopiedLink] = useState<string | null>(null);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    // Fetch members
    const { data: membersData, isLoading: loadingMembers } = useQuery({
        queryKey: ['workspace-members', workspace?.id],
        queryFn: () => api.getWorkspaceMembers(workspace!.id),
        enabled: !!workspace?.id,
    });

    // Fetch pending invitations
    const { data: invitationsData } = useQuery({
        queryKey: ['workspace-invitations', workspace?.id],
        queryFn: () => api.getWorkspaceInvitations(workspace!.id),
        enabled: !!workspace?.id && (membersData?.currentUserRole === 'owner' || membersData?.currentUserRole === 'admin'),
    });

    // Invite mutation
    const inviteMutation = useMutation({
        mutationFn: (data: { email: string; role: 'admin' | 'member' | 'viewer' }) =>
            api.inviteMember(workspace!.id, data.email, data.role),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workspace-invitations'] });
            setShowInviteModal(false);
            setInviteEmail('');
            setInviteRole('member');
        },
    });

    // Change role mutation
    const changeRoleMutation = useMutation({
        mutationFn: (data: { memberId: string; role: 'admin' | 'member' | 'viewer' }) =>
            api.changeMemberRole(workspace!.id, data.memberId, data.role),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
            setActiveDropdown(null);
        },
    });

    // Remove member mutation
    const removeMemberMutation = useMutation({
        mutationFn: (memberId: string) => api.removeMember(workspace!.id, memberId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
            setActiveDropdown(null);
        },
    });

    // Cancel invitation mutation
    const cancelInvitationMutation = useMutation({
        mutationFn: (invitationId: string) => api.cancelInvitation(workspace!.id, invitationId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workspace-invitations'] });
        },
    });

    // Delete workspace mutation
    const navigate = useNavigate();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const deleteWorkspaceMutation = useMutation({
        mutationFn: () => api.deleteWorkspace(workspace!.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workspaces'] });
            navigate('/dashboard');
        },
    });

    const canInvite = membersData?.currentUserRole === 'owner' || membersData?.currentUserRole === 'admin';
    const canManageRoles = membersData?.currentUserRole === 'owner' || membersData?.currentUserRole === 'admin';
    const memberCount = membersData?.members?.length || 0;
    const memberLimit = membersData?.memberLimit || 5;
    const atLimit = memberCount >= memberLimit;

    const handleCopyLink = async (link: string) => {
        await navigator.clipboard.writeText(link);
        setCopiedLink(link);
        setTimeout(() => setCopiedLink(null), 2000);
    };

    if (!workspace) {
        return (
            <div className="p-8 text-center text-neutral-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                <p>{t('team.noWorkspace', 'Không tìm thấy workspace')}</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Users className="w-6 h-6 text-primary-600" />
                        {t('team.title', 'Quản lý Team')}
                    </h1>
                    <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                        {t('team.subtitle', 'Mời và quản lý thành viên trong workspace của bạn')}
                    </p>
                </div>

                {canInvite && (
                    <button
                        onClick={() => setShowInviteModal(true)}
                        disabled={atLimit}
                        className={clsx(
                            'btn-primary gap-2',
                            atLimit && 'opacity-50 cursor-not-allowed'
                        )}
                    >
                        <UserPlus className="w-4 h-4" />
                        {t('team.invite', 'Mời thành viên')}
                    </button>
                )}
            </div>

            {/* Member Count Card */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-neutral-500">
                            {t('team.memberCount', 'Số thành viên')}
                        </p>
                        <p className="text-2xl font-bold">
                            {memberCount} <span className="text-neutral-400 text-lg">/ {memberLimit}</span>
                        </p>
                    </div>
                    {atLimit && (
                        <div className="text-sm text-warning-600 bg-warning-50 dark:bg-warning-600/10 px-3 py-2 rounded-lg">
                            {t('team.limitReached', 'Đã đạt giới hạn. Nâng cấp để thêm thành viên.')}
                        </div>
                    )}
                </div>
                <div className="mt-2 h-2 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                    <div
                        className={clsx(
                            'h-full rounded-full transition-all',
                            atLimit ? 'bg-warning-500' : 'bg-primary-500'
                        )}
                        style={{ width: `${(memberCount / memberLimit) * 100}%` }}
                    />
                </div>
            </div>

            {/* Members List */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="font-semibold">{t('team.members', 'Thành viên')} ({memberCount})</h2>
                </div>

                {loadingMembers ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" />
                    </div>
                ) : (
                    <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                        {membersData?.members?.map((member) => {
                            const config = roleConfig[member.role as Role];
                            const Icon = config?.icon || User;
                            const isCurrentUser = member.user_id === currentUser?.id;
                            // Owner can edit anyone except owner, admin can only edit member/viewer
                            const isOwner = membersData?.currentUserRole === 'owner';
                            const canEdit = canManageRoles && member.role !== 'owner' && !isCurrentUser &&
                                (isOwner || (member.role !== 'admin'));
                            const canRemove = (canInvite && member.role !== 'owner' && !isCurrentUser) || isCurrentUser;

                            return (
                                <div key={member.id} className="p-4 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                                    <div className="flex items-center gap-3">
                                        {/* Avatar */}
                                        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-600/20 flex items-center justify-center">
                                            {member.avatar_url ? (
                                                <img src={member.avatar_url} className="w-10 h-10 rounded-full" alt="" />
                                            ) : (
                                                <span className="text-lg font-semibold text-primary-600">
                                                    {member.full_name?.charAt(0).toUpperCase()}
                                                </span>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div>
                                            <p className="font-medium flex items-center gap-2">
                                                {member.full_name}
                                                {isCurrentUser && (
                                                    <span className="text-xs text-neutral-500">(bạn)</span>
                                                )}
                                            </p>
                                            <p className="text-sm text-neutral-500">{member.email}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {/* Role Badge */}
                                        <div className={clsx(
                                            'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium',
                                            config?.color
                                        )}>
                                            <Icon className="w-3.5 h-3.5" />
                                            {config?.label}
                                        </div>

                                        {/* Actions */}
                                        {(canEdit || canRemove) && (
                                            <div className="relative">
                                                <button
                                                    onClick={() => setActiveDropdown(activeDropdown === member.user_id ? null : member.user_id)}
                                                    className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-600 rounded-lg"
                                                >
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>

                                                {activeDropdown === member.user_id && (
                                                    <div className="absolute right-0 top-full mt-1 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 min-w-[160px] z-50">
                                                        {canEdit && (
                                                            <>
                                                                <p className="px-3 py-1 text-xs text-neutral-500 uppercase">
                                                                    {t('team.changeRole', 'Đổi vai trò')}
                                                                </p>
                                                                {(['admin', 'member', 'viewer'] as const).filter(r => r !== member.role).map((role) => (
                                                                    <button
                                                                        key={role}
                                                                        onClick={() => changeRoleMutation.mutate({ memberId: member.user_id, role })}
                                                                        className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
                                                                    >
                                                                        {roleConfig[role].label}
                                                                    </button>
                                                                ))}
                                                                <div className="border-t border-neutral-200 dark:border-neutral-700 my-1" />
                                                            </>
                                                        )}
                                                        {canRemove && (
                                                            <button
                                                                onClick={() => removeMemberMutation.mutate(member.user_id)}
                                                                className="w-full px-3 py-2 text-left text-sm text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-600/10 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                {isCurrentUser ? t('team.leave', 'Rời workspace') : t('team.remove', 'Xóa thành viên')}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Pending Invitations */}
            {canInvite && invitationsData?.invitations && invitationsData.invitations.length > 0 && (
                <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                    <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
                        <h2 className="font-semibold">{t('team.pendingInvitations', 'Lời mời đang chờ')} ({invitationsData.invitations.length})</h2>
                    </div>

                    <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                        {invitationsData.invitations.map((invitation) => (
                            <div key={invitation.id} className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
                                        <Mail className="w-5 h-5 text-neutral-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium">{invitation.email}</p>
                                        <p className="text-sm text-neutral-500">
                                            {t('team.invitedBy', 'Mời bởi')} {invitation.invited_by_name}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className={clsx(
                                        'px-2.5 py-1 rounded-full text-sm font-medium',
                                        roleConfig[invitation.role as Role]?.color || 'bg-neutral-100'
                                    )}>
                                        {roleConfig[invitation.role as Role]?.label || invitation.role}
                                    </span>
                                    <button
                                        onClick={() => cancelInvitationMutation.mutate(invitation.id)}
                                        className="p-2 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-600/10 rounded-lg"
                                        title={t('team.cancelInvite', 'Hủy lời mời')}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-md p-6">
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-primary-600" />
                            {t('team.inviteMember', 'Mời thành viên mới')}
                        </h2>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            inviteMutation.mutate({ email: inviteEmail, role: inviteRole });
                        }}>
                            <div className="space-y-4">
                                <div>
                                    <label className="label">{t('team.email', 'Email')}</label>
                                    <input
                                        type="email"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        placeholder="name@example.com"
                                        className="input"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="label">{t('team.role', 'Vai trò')}</label>
                                    <div className="relative">
                                        <select
                                            value={inviteRole}
                                            onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)}
                                            className="input appearance-none pr-10"
                                        >
                                            <option value="member">{roleConfig.member.label}</option>
                                            <option value="admin">{roleConfig.admin.label}</option>
                                            <option value="viewer">{roleConfig.viewer.label}</option>
                                        </select>
                                        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400" />
                                    </div>
                                </div>
                            </div>

                            {inviteMutation.isError && (
                                <div className="mt-4 p-3 bg-danger-50 dark:bg-danger-600/10 text-danger-600 rounded-lg text-sm flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    {(inviteMutation.error as Error)?.message || t('team.inviteError', 'Không thể gửi lời mời')}
                                </div>
                            )}

                            {inviteMutation.isSuccess && inviteMutation.data?.invitation && (
                                <div className="mt-4 p-3 bg-success-50 dark:bg-success-600/10 rounded-lg text-sm">
                                    <p className="text-success-700 dark:text-success-400 mb-2">
                                        {t('team.inviteSent', 'Đã gửi lời mời!')}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={inviteMutation.data.invitation.inviteLink}
                                            readOnly
                                            className="input text-xs flex-1"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleCopyLink(inviteMutation.data.invitation.inviteLink)}
                                            className="btn-secondary p-2"
                                        >
                                            {copiedLink === inviteMutation.data.invitation.inviteLink ? (
                                                <Check className="w-4 h-4 text-success-600" />
                                            ) : (
                                                <Copy className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowInviteModal(false);
                                        inviteMutation.reset();
                                    }}
                                    className="btn-secondary flex-1"
                                >
                                    {t('common.cancel', 'Hủy')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={inviteMutation.isPending || !inviteEmail}
                                    className="btn-primary flex-1 gap-2"
                                >
                                    {inviteMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Mail className="w-4 h-4" />
                                    )}
                                    {t('team.sendInvite', 'Gửi lời mời')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Danger Zone - Delete Workspace */}
            {membersData?.currentUserRole === 'owner' && (
                <div className="bg-white dark:bg-neutral-800 rounded-xl border border-danger-200 dark:border-danger-800">
                    <div className="p-4 border-b border-danger-200 dark:border-danger-800">
                        <h2 className="font-semibold text-danger-600">{t('team.dangerZone', 'Vùng nguy hiểm')}</h2>
                    </div>
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium">{t('team.deleteWorkspace', 'Xóa workspace')}</p>
                                <p className="text-sm text-neutral-500">
                                    {t('team.deleteWorkspaceDesc', 'Xóa vĩnh viễn workspace này cùng tất cả dữ liệu. Hành động này không thể hoàn tác.')}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="btn-ghost text-danger-600 border border-danger-300 dark:border-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20 flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" />
                                {t('team.delete', 'Xóa')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-md p-6">
                        <h2 className="text-xl font-bold text-danger-600 mb-4">
                            {t('team.confirmDelete', 'Xác nhận xóa workspace')}
                        </h2>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                            {t('team.deleteWarning', 'Hành động này sẽ xóa vĩnh viễn:')}
                        </p>
                        <ul className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 list-disc list-inside">
                            <li>Tất cả Objectives và Key Results</li>
                            <li>Tất cả Boards và Tasks</li>
                            <li>Tất cả thành viên và lời mời</li>
                            <li>Lịch sử chat và hoạt động</li>
                        </ul>
                        <p className="text-sm mb-2">
                            Nhập <strong>{workspace?.name}</strong> để xác nhận:
                        </p>
                        <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            className="input mb-4"
                            placeholder={workspace?.name}
                        />
                        {deleteWorkspaceMutation.isError && (
                            <div className="mb-4 p-3 bg-danger-50 dark:bg-danger-600/10 text-danger-600 rounded-lg text-sm">
                                {(deleteWorkspaceMutation.error as Error)?.message || 'Không thể xóa workspace'}
                            </div>
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeleteConfirmText('');
                                }}
                                className="btn-secondary flex-1"
                            >
                                {t('common.cancel', 'Hủy')}
                            </button>
                            <button
                                onClick={() => deleteWorkspaceMutation.mutate()}
                                disabled={deleteConfirmText !== workspace?.name || deleteWorkspaceMutation.isPending}
                                className="btn-primary bg-danger-600 hover:bg-danger-700 flex-1 disabled:opacity-50"
                            >
                                {deleteWorkspaceMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    t('team.deleteForever', 'Xóa vĩnh viễn')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Click outside to close dropdown */}
            {activeDropdown && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setActiveDropdown(null)}
                />
            )}
        </div>
    );
}
